import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CacheService } from '../../../../../../shared/services/cache.service';
import { DropdownToggleService } from '../../../../../../shared/services/dropdown-toggle.service';
import { AppRoutesUtils } from '../../../../../../utils/app-routes-util';
import { FormatterUtil } from '../../../../../../utils/formatter-util';
import { AssetGalleryParams } from '../../../../../../shared/models/asset-gallery-params';
import { FilterOptionsEnum } from '../../../../../../shared/models/filter-options-enum';
import { Category } from '../../../../../components/models/pack';
import { PublicService } from '../../../../services/public.service';
import {
  BROWSE_KEYWORD_SEARCH_TYPES,
  BrowseKeywordCategory,
  KeywordSearchType,
} from '../../../../models/browse-keyword-category';
import { SearchLevelEnum } from '../../../pages/gallery/asset-gallery/models/search-level-enum';
import { browseModeFromSearchType, formatBrowseKeywordLabel } from './browse-keyword-route';
import { BROWSE_ASSETS_KEYWORDS_DATA } from './browse-assets-keywords.data';

@Component({
  selector: 'app-browse-assets-by-keywords',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './browse-assets-by-keywords.component.html',
  styleUrl: './browse-assets-by-keywords.component.css',
})
export class BrowseAssetsByKeywordsComponent implements OnInit {
  categories: Category[] = [];
  /** Tabs always come from category cache (parent categories). */
  categoryTabs: string[] = [];
  categoriesData: BrowseKeywordCategory[] = [];
  readonly searchTypes: KeywordSearchType[] = BROWSE_KEYWORD_SEARCH_TYPES;
  readonly searchLevelEnum = SearchLevelEnum;
  readonly filterOptionsEnum = FilterOptionsEnum;
  readonly searchLevelDropdownId = 'browseLandingSearchLevel';

  activeTab = '';
  selectedSearchType: KeywordSearchType = 'Top searches';
  selectedsearchLevel: string = SearchLevelEnum.Individual;
  /** Signals so zoneless CD picks up async updates without a click. */
  readonly loading = signal(true);
  /** 10 rows × 6 columns keyword skeleton placeholders. */
  readonly keywordLoaderItems = Array.from({ length: 60 }, (_, i) => i);

  constructor(
    private publicService: PublicService,
    private cacheService: CacheService,
    private dropdownService: DropdownToggleService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.init();
  }

  get visibleKeywords(): string[] {
    const category = this.categoriesData.find((c) => c.name === this.activeTab);
    if (!category) {
      return [];
    }
    return this.selectedSearchType === 'Top searches'
      ? category.topSearches
      : category.trendingSearches;
  }

  setTab(tab: string): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
  }

  activeClass(tab: string): string {
    return tab === this.activeTab
      ? 'text-themeBlackTwo border-b-[3px] border-[#FECB2F]'
      : '';
  }

  searchTypeClass(type: KeywordSearchType): string {
    return type === this.selectedSearchType ? 'bg-black text-white border-[#121212]' : 'bg-white text-black border-[#E3E3E7]';
  }

  onSelectSearchType(type: KeywordSearchType): void {
    this.selectedSearchType = type;
  }

  displayKeyword(keyword: string): string {
    return formatBrowseKeywordLabel(
      keyword,
      this.activeTab,
      this.selectedsearchLevel === SearchLevelEnum.Pack
    );
  }

  getKeywordQueryParams(): Record<string, string> {
    const query: Record<string, string> = {};
    if (this.selectedsearchLevel === SearchLevelEnum.Pack) {
      query['searchLevel'] = FormatterUtil.formatToKebabCase(this.selectedsearchLevel);
    }
    return query;
  }

  getKeywordLink(keyword: string): string[] | null {
    const params: AssetGalleryParams = {
      category: this.activeTab,
      search: keyword,
    };
    return AppRoutesUtils.Asset.getAssetGalleryViaParams(params);
  }

  getViewMoreLink(): string[] | null {
    return AppRoutesUtils.BrowseKeywords.getCategoryPath(
      this.activeTab,
      browseModeFromSearchType(this.selectedSearchType),
      this.selectedsearchLevel
    );
  }

  toggleDropdown(id: string, event: Event): void {
    event.stopPropagation();
    this.dropdownService.toggleDropdown(id);
    this.cdr.detectChanges();
  }

  isDropdownVisible(id: string): boolean {
    return this.dropdownService.isDropdownVisible(id);
  }

  dropdownPanelClasses(id: string): string[] {
    const visibility = this.isDropdownVisible(id)
      ? 'opacity-100 translate-y-0 visible'
      : 'translate-y-7 invisible';
    return [visibility, 'right-0 left-auto'];
  }

  onSelectSearchLevel(searchLevel: string): void {
    this.selectedsearchLevel =
      searchLevel === FilterOptionsEnum.None ? SearchLevelEnum.Individual : searchLevel;
    this.dropdownService.hideDropdown(this.searchLevelDropdownId);
    this.cdr.detectChanges();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.dropdownService.hideDropdown(this.searchLevelDropdownId);
  }

  private async init(): Promise<void> {
    await this.loadCategoriesFromCache();
    this.loadBrowseKeywords();
  }

  private async loadCategoriesFromCache(): Promise<void> {
    try {
      const response = await firstValueFrom(this.cacheService.getCategoriesFromCache());
      this.categories = (response ?? []).filter((c) => c.parentID === -1 || c.parentID == null);
      this.categoryTabs = this.categories
        .map((c) => c.name)
        .filter((name): name is string => !!name?.trim());

      // Seed empty keyword shells in cache category order (fallback fills later if API empty).
      this.categoriesData = this.categoryTabs.map((name) => {
        const fallback = BROWSE_ASSETS_KEYWORDS_DATA.find(
          (c) => c.name.toLowerCase() === name.toLowerCase()
        );
        return {
          name,
          topSearches: fallback?.topSearches ?? [],
          trendingSearches: fallback?.trendingSearches ?? [],
        };
      });

      this.activeTab = this.categoryTabs[0] ?? '';
    } catch {
      this.categoryTabs = BROWSE_ASSETS_KEYWORDS_DATA.map((c) => c.name);
      this.categoriesData = [...BROWSE_ASSETS_KEYWORDS_DATA];
      this.activeTab = this.categoryTabs[0] ?? '';
    }
  }

  private loadBrowseKeywords(): void {
    this.loading.set(true);
    this.publicService.getBrowseSearchKeywords().subscribe({
      next: (response) => {
        const mapped = (response ?? []).map((item) => ({
          name: item.name,
          topSearches: item.topSearches ?? [],
          trendingSearches: item.trendingSearches ?? [],
        }));

        // Keep tabs from cache; only attach keyword lists to matching category names.
        this.categoriesData = this.mergeOntoCacheCategories(mapped);
        this.loading.set(false);
      },
      error: () => {
        // Keep cache-category shells + hardcoded fallback already seeded.
        this.loading.set(false);
      },
    });
  }

  /** Map API keyword lists onto cached category tabs (order/names stay from cache). */
  private mergeOntoCacheCategories(apiData: BrowseKeywordCategory[]): BrowseKeywordCategory[] {
    const apiByName = new Map(
      apiData.map((c) => [c.name.toLowerCase(), c])
    );
    const fallbackByName = new Map(
      BROWSE_ASSETS_KEYWORDS_DATA.map((c) => [c.name.toLowerCase(), c])
    );

    return this.categoryTabs.map((name) => {
      const api = apiByName.get(name.toLowerCase());
      const fallback = fallbackByName.get(name.toLowerCase());
      const top =
        api?.topSearches?.length ? api.topSearches : (fallback?.topSearches ?? []);
      const trending =
        api?.trendingSearches?.length
          ? api.trendingSearches
          : (fallback?.trendingSearches ?? []);

      return {
        name,
        topSearches: top,
        trendingSearches: trending,
      };
    });
  }
}
